/**
 * PreferenceExpandableCard
 *
 * Collapsible card with icon, label, and description.
 * Shows short summary by default, expands to show full description.
 */
import { useState } from 'react'
import { Box, HStack, Text, VStack, Collapse } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, Globe, Sparkles, Target, type LucideIcon } from 'lucide-react'
import { haptics } from '@/utils/haptics'
import type { CuisineCuriosity, AtmospherePreference, TasteVariance } from '@/types/tasteInsights'

const MotionBox = motion.create(Box)

export type PreferenceType = 'cuisineCuriosity' | 'atmosphere' | 'tasteVariance'

/** Union type for all expandable preference value types */
type PreferenceValue = CuisineCuriosity | AtmospherePreference | TasteVariance

interface PreferenceExpandableCardProps {
  /** Type of preference (determines icon and label) */
  type: PreferenceType
  /** The structured preference data */
  value: PreferenceValue
  /** Initial expanded state */
  defaultExpanded?: boolean
}

const preferenceConfig: Record<PreferenceType, { icon: LucideIcon; label: string; color: string }> = {
  cuisineCuriosity: {
    icon: Globe,
    label: 'Cuisine Curiosity',
    color: 'blue.400',
  },
  atmosphere: {
    icon: Sparkles,
    label: 'Atmosphere',
    color: 'purple.400',
  },
  tasteVariance: {
    icon: Target,
    label: 'Taste Variance',
    color: 'teal.400',
  },
}

/**
 * Extract the summary/header text from the structured value based on type
 */
function getSummary(type: PreferenceType, value: PreferenceValue): string {
  switch (type) {
    case 'cuisineCuriosity':
      return (value as CuisineCuriosity).summary
    case 'atmosphere':
      return (value as AtmospherePreference).style
    case 'tasteVariance':
      return (value as TasteVariance).frequency
  }
}

export function PreferenceExpandableCard({
  type,
  value,
  defaultExpanded = false,
}: PreferenceExpandableCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const config = preferenceConfig[type]
  const Icon = config.icon
  const summary = getSummary(type, value)
  const description = value.description

  const handleToggle = async () => {
    await haptics.selection()
    setIsExpanded(!isExpanded)
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        bg="var(--card-surface-2)"
        borderRadius="var(--r-card-compact)"
        overflow="hidden"
        transition="background 0.2s"
        _hover={{ bg: 'var(--card-chip-hover)' }}
      >
        {/* Header - always visible, clickable */}
        <HStack
          p={3}
          cursor="pointer"
          onClick={handleToggle}
          justify="space-between"
        >
          <HStack spacing={2} flex={1} minW={0}>
            <Box color={config.color} flexShrink={0}>
              <Icon size={16} />
            </Box>
            <VStack align="start" spacing={0} flex={1} minW={0}>
              <Text fontSize="sm" color="var(--card-text-2)">
                {config.label}
              </Text>
              <Text
                fontSize="sm"
                fontWeight="medium"
                color="var(--card-text)"
                noOfLines={2}
              >
                {summary}
              </Text>
            </VStack>
          </HStack>
          <Box color="var(--card-text-2)" flexShrink={0}>
            <AnimatePresence mode="wait">
              {isExpanded ? (
                <motion.div
                  key="up"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronUp size={18} />
                </motion.div>
              ) : (
                <motion.div
                  key="down"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={18} />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </HStack>

        {/* Expandable content */}
        <Collapse in={isExpanded} animateOpacity>
          <Box
            px={3}
            pb={3}
            pt={0}
          >
            <Box
              borderTop="1px solid"
              borderColor="var(--card-divider)"
              pt={2}
            >
              <Text fontSize="sm" color="var(--card-text)" lineHeight="tall">
                {description}
              </Text>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </MotionBox>
  )
}
